import { useEffect, useState } from 'react';
import { SettingsSection, DataTable, Toggle, TextInput, FormField, FormSelect, StatusBadge } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Icon } from '@/components/ui/Icon';
import { adminApi } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';

const DAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

interface BH { day_of_week: number; open?: string; close?: string; enabled: boolean }
interface Holiday { id: number; name: string; date: string; action?: string }

/** שעות פעילות + חגים — לשונית במסך ההגדרות (המסך העצמאי בוטל). */
export function HoursTab() {
  const [hours, setHours] = useState<BH[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [newHoliday, setNewHoliday] = useState({ name: '', date: '', action: 'closed' });
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { void load(); }, []);

  async function load() {
    const data = (await adminApi.hours()) as { hours: BH[]; holidays: Holiday[] };
    if (data.hours && data.hours.length === 7) {
      setHours(data.hours);
    } else {
      setHours(Array.from({ length: 7 }, (_, i) => ({ day_of_week: i, open: '08:00', close: '18:00', enabled: i < 6 })));
    }
    setHolidays(data.holidays ?? []);
  }

  async function save() {
    setSaving(true);
    try {
      await adminApi.upsertHours(hours);
      toast('נשמר', 'ok');
    } catch {
      toast('שגיאה בשמירת שעות הפעילות', 'danger');
    } finally {
      setSaving(false);
    }
  }

  function updateRow(idx: number, patch: Partial<BH>) {
    setHours((prev) => prev.map((h, i) => (i === idx ? { ...h, ...patch } : h)));
  }

  // Copies the first enabled day's open/close to every other enabled day —
  // the common "same hours every day" case, without retyping 7 rows.
  function applyToAll() {
    const src = hours.find((h) => h.enabled) ?? hours[0];
    if (!src) return;
    setHours((prev) => prev.map((h) => (h.enabled ? { ...h, open: src.open, close: src.close } : h)));
    toast('הועתק לכל הימים הפעילים', 'ok');
  }

  async function addHoliday() {
    if (!newHoliday.name || !newHoliday.date) {
      toast('יש למלא שם ותאריך', 'danger');
      return;
    }
    setAdding(true);
    try {
      await adminApi.addHoliday(newHoliday);
      toast('החג נוסף', 'ok');
      setNewHoliday({ name: '', date: '', action: 'closed' });
      void load();
    } catch {
      toast('שגיאה בהוספת חג', 'danger');
    } finally {
      setAdding(false);
    }
  }

  async function removeHoliday(id: number) {
    try {
      await adminApi.deleteHoliday(id);
      toast('החג נמחק', 'ok');
      setHolidays((prev) => prev.filter((h) => h.id !== id));
    } catch {
      toast('שגיאה במחיקה', 'danger');
    }
  }

  const holidayCols: DataTableColumn<Holiday>[] = [
    { key: 'name', he: 'שם', render: (h) => h.name },
    { key: 'date', he: 'תאריך', render: (h) => <span dir="ltr">{h.date}</span> },
    {
      key: 'action', he: 'פעולה',
      render: (h) => (
        <StatusBadge tone={h.action === 'reduced' ? 'warn' : 'neutral'} size="sm">
          {h.action === 'reduced' ? 'שעות מצומצמות' : 'סגור'}
        </StatusBadge>
      ),
    },
    {
      key: '_actions', he: '',
      render: (h) => (
        <button type="button" className="btn ghost" style={{ color: 'var(--danger)' }} aria-label={`מחק ${h.name}`} onClick={() => void removeHoliday(h.id)}>
          <Icon name="trash" size={14} />
        </button>
      ),
    },
  ];

  return (
    <div className="stack">
      <SettingsSection
        title="שעות פעילות שבועיות"
        icon="clock"
        description="בשעות שמחוץ לטווח, שיחות נכנסות מנותבות למסלול 'סגור' שהוגדר ב-IVR."
        action={
          <button type="button" className="btn" onClick={applyToAll} title="העתק את שעות היום הפעיל הראשון לכל שאר הימים">
            <Icon name="copy" size={13} /> החל על כל הימים
          </button>
        }
      >
        <div className="stack" style={{ gap: 4 }}>
          {/* Column headers */}
          <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 1fr 120px', gap: 12, padding: '0 4px', fontSize: 'var(--t-xs)', color: 'var(--muted)', fontWeight: 600 }}>
            <span>יום</span>
            <span>פתיחה</span>
            <span>סגירה</span>
            <span>סטטוס</span>
          </div>
          {hours.map((h, i) => (
            <div
              key={i}
              style={{
                display: 'grid', gridTemplateColumns: '110px 1fr 1fr 120px', gap: 12, alignItems: 'center',
                padding: '8px 4px', borderRadius: 'var(--r-2)',
                background: h.enabled ? 'transparent' : 'var(--surface-2)',
                opacity: h.enabled ? 1 : 0.7,
              }}
            >
              <strong style={{ fontSize: 'var(--t-sm)' }}>{DAYS[h.day_of_week]}</strong>
              <TextInput type="time" value={h.open ?? ''} onChange={(e) => updateRow(i, { open: e.target.value })} dir="ltr" disabled={!h.enabled} />
              <TextInput type="time" value={h.close ?? ''} onChange={(e) => updateRow(i, { close: e.target.value })} dir="ltr" disabled={!h.enabled} />
              <Toggle checked={h.enabled} onChange={(v) => updateRow(i, { enabled: v })} label={h.enabled ? 'פעיל' : 'סגור'} />
            </div>
          ))}
          <div style={{ marginTop: 8 }}>
            <button type="button" className="btn primary" onClick={save} disabled={saving}>{saving ? 'שומר…' : 'שמור שעות'}</button>
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        title="חגים וחופשות"
        icon="calendar"
        badge={holidays.length > 0 ? { label: holidays.length, tone: 'neutral' } : undefined}
        description="בתאריכים אלו המרכזייה תתנהג כ'סגור' או בשעות מצומצמות, גם אם הם ימי פעילות רגילים."
      >
        <div className="stack">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px 160px auto', gap: 8, alignItems: 'end' }}>
            <FormField label="שם החג"><TextInput value={newHoliday.name} onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })} placeholder="לדוגמה: ראש השנה" /></FormField>
            <FormField label="תאריך"><TextInput type="date" value={newHoliday.date} onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })} dir="ltr" /></FormField>
            <FormField label="פעולה">
              <FormSelect value={newHoliday.action} onChange={(e) => setNewHoliday({ ...newHoliday, action: e.target.value })}>
                <option value="closed">סגור</option>
                <option value="reduced">שעות מצומצמות</option>
              </FormSelect>
            </FormField>
            <button type="button" className="btn primary" onClick={addHoliday} disabled={adding}>{adding ? 'מוסיף…' : '+ הוסף'}</button>
          </div>

          <DataTable<Holiday>
            columns={holidayCols}
            rows={holidays}
            emptyMessage="לא הוגדרו חגים."
          />
        </div>
      </SettingsSection>
    </div>
  );
}
